import { effect, signal, Signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { switchMap } from 'rxjs'
import { FormGroup } from '@angular/forms'
import { FreetextForm, FreetextSection } from '@features/certificate/models/certificate-shared.model'
import { hasActualValue, onValueChanges } from '@core/utils/form-utils'

/**
 * Concise factory for a single freetext section definition.
 *
 * Collapses the repetitive object literal (which SonarQube flags as duplicated across the
 * provisions components) into a single call, while each component still lists exactly the
 * sections it wants to display. The `enabled` signal is always created fresh, defaulting to false.
 */
export function freetextSection<TKey extends string>(
  key: TKey,
  freetextLabel: string,
  dataTestSlug: string,
  generalLabel?: string
): FreetextSection<TKey> {
  return {
    key,
    freetextLabel,
    dataTestSlug,
    required: true,
    ...(generalLabel === undefined ? {} : { generalLabel }),
    enabled: signal<boolean>(false)
  }
}

type CoverType = 'liability' | 'hull'

/**
 * Shared "Additional Insured" section used by both hull- and liability-cover-provisions.
 * The freetext label differs slightly between the two, so it is parametrised.
 */
export function additionalInsuredSection(
  coverType: CoverType,
  freetextLabel = 'Additional Insured Freetext'
): FreetextSection<'additionalInsured'> {
  const title = coverType === 'hull'
    ? 'Additional Insured (US specific)'
    : 'Additional Insured'

  return freetextSection('additionalInsured', freetextLabel, 'additional-insured', title)
}

/**
 * Shared "No Liability for Premiums" section. The general label differs between the two
 * components (hull appends "(General Confirmation)"), so it is parametrised.
 */
export function noLiabilityForPremiumsSection(
  generalLabel = 'No Liability for Premiums'
): FreetextSection<'noLiabilityForPremiums'> {
  return freetextSection('noLiabilityForPremiums', 'No Liability for Premiums Freetext', 'no-liability-for-premiums', generalLabel)
}

/**
 * Shared "Breach of Warranty" section (identical in both components).
 */
export function breachOfWarrantySection(): FreetextSection<'breachOfWarranty'> {
  return freetextSection('breachOfWarranty', 'Breach of Warranty Freetext', 'breach-of-warranty', 'Breach of Warranty')
}

/**
 * Shared "Notice of Cancellation (No War)" section. The data-test slug differs between the two
 * components, so it is parametrised.
 */
export function noticeOfCancellationNoWarSection(
  dataTestSlug = 'notice-of-cancellation-no-war'
): FreetextSection<'noticeOfCancellationNoWar'> {
  return freetextSection('noticeOfCancellationNoWar', 'Notice of Cancellation Freetext', dataTestSlug, 'Notice of Cancellation (no War)')
}

/**
 * Wires up the shared freetext-section behaviour without forcing components into a common base class.
 *
 * Each component still declares its own `freetextSections` array (deciding exactly which fields it
 * wants to display); this helper only takes care of the repetitive plumbing:
 *  - syncing every section's `enabled` signal from its `freetextCheckbox` form control, and
 *  - clearing the freetext value whenever a section becomes disabled.
 *
 * It must be called from within an injection context (e.g. a component constructor) because it
 * registers an Angular effect and follows the form input.
 *
 * @returns a `key -> FreetextSection` lookup for convenient template access.
 */
export function setupFreetextSections<TKey extends string>(
  form: Signal<FormGroup>,
  sections: FreetextSection<TKey>[]
): Record<TKey, FreetextSection> {
  let initialized = false

  // Adopt the persisted `freetextCheckbox` value into each section's `enabled` signal.
  // This must run on initial load and whenever the form is (re)patched with backend data,
  // but it must NOT override a value the user just toggled. Since typing into a freetext or
  // toggling the general checkbox does not change any `freetextCheckbox` control, we only
  // pull a value in when it actually differs from the current signal - that only happens on
  // an external patch, never as a side effect of the user opening/closing a section here.
  toObservable(form).pipe(
    switchMap(currentForm => onValueChanges(currentForm, currentForm)),
    takeUntilDestroyed()
  ).subscribe(currentForm => {
    for (const section of sections) {
      const group = currentForm.controls[section.key] as FormGroup<FreetextForm>
      const persisted = hasActualValue(group.controls.freetextCheckbox.value)
      // Compare against `enabled` only on form (patch) changes, never when the user toggles it here -
      // otherwise toggling a section on would see the mismatch before the write-back below has set
      // `freetextCheckbox` and immediately reset `enabled` to false (freetext never opens).
      if (persisted !== section.enabled()) {
        section.enabled.set(persisted)
      }
    }
    initialized = true
  })

  // Persist each section's `enabled` state back into its `freetextCheckbox` control and clear
  // the freetext when the section is disabled. Writing back keeps the form the single source
  // of truth so the sync above stays consistent (this is what stops a freetext from
  // collapsing while typing, or all sections collapsing when an unrelated control changes).
  effect(() => {
    const currentForm = form()
    for (const section of sections) {
      const enabled = section.enabled()
      if (!initialized) continue

      const group = currentForm.controls[section.key] as FormGroup<FreetextForm>
      const checkboxChanged = hasActualValue(group.controls.freetextCheckbox.value) !== enabled
      if (checkboxChanged) {
        group.controls.freetextCheckbox.setValue(enabled, { emitEvent: false })
      }

      if (!enabled) {
        const freetextCtrl = group.controls.freetext
        if (freetextCtrl.value !== '' && freetextCtrl.value !== null) {
          freetextCtrl.setValue('', { emitEvent: false })
        }
      }
      if (checkboxChanged) {
        group.controls.freetext.updateValueAndValidity()
      }
    }
  })

  return sections.reduce((acc, section) => {
    acc[section.key] = section
    return acc
  }, {} as Record<TKey, FreetextSection>)
}
